/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {makeStyles} from '@material-ui/core';
import {translate} from '@ion/core/src/locale/locale';
import {ImpactTypeIcon} from '@ion/dls/src/icons/ImpactTypeIcon';
import {themedClassName} from '@ion/dls/src/theme/helpers';
import {dlsColorCondition} from '@ion/dls/src/theme/theme';
import {isCurrentTheme} from '@ion/dls/src/theme/useThemeContext';

interface ShockSensorGraphicProps {
  impactArea: string;
  isOverThreshold: boolean;
}

const IMPACT_AREA = {
  BOTTOM : 'drop',
  TOP    : 'impact'
};

// the uppercase impact label typography the top/bottom and side labels share ...
const impactLabelStyles = {
  fontSize      : '10px',
  fontFamily    : 'Roboto',
  fontWeight    : 500,
  letterSpacing : '1px',
  textTransform : 'uppercase' as const
};

// the side labels' rotated base - each side only adds its own offset ...
const sideLabelStyles = {
  ...impactLabelStyles,
  transform : 'rotate(270deg)',
  position  : 'absolute' as const,
  top       : '30%'
};

const useStyles = makeStyles(() => {
  return {
    container : {
      paddingTop    : '24px',
      display       : 'flex',
      flexDirection : 'column',
      alignItems    : 'center',
      maxWidth      : '150px'
    },
    impactLabel : {
      ...impactLabelStyles
    },
    row : {
      display        : 'flex',
      alignItems     : 'center',
      justifyContent : 'center',
      position       : 'relative',
      margin         : 8
    },
    left : {
      ...sideLabelStyles,
      left : -4
    },
    right : {
      ...sideLabelStyles,
      right : -4
    },
    // current theme matches the shared uppercase label's weight/line-height
    // - severity color stays the conditional inline style it already is ...
    currentLabel : {
      fontWeight : 700,
      lineHeight : '14px'
    }
  };
});

const ShockSensorGraphic = ({impactArea, isOverThreshold}: ShockSensorGraphicProps) => {
  const classes           = useStyles();
  const isCurrent         = isCurrentTheme();
  const isDrop            = impactArea === IMPACT_AREA.BOTTOM;
  const isImpact          = impactArea === IMPACT_AREA.TOP;
  const topImpactColor    = dlsColorCondition(isOverThreshold && isImpact, 'caRed', 'grey2');
  const bottomImpactColor = dlsColorCondition(isOverThreshold && isDrop, 'caRed', 'grey2');

  return (
    <div className={classes.container}>
      <span className={themedClassName(classes.impactLabel, classes.currentLabel, isCurrent)}
        style={{color: topImpactColor}}>
        {translate('tracking:scShockSensor.top')}
      </span>
      <div className={classes.row}>
        <span className={themedClassName(classes.left, classes.currentLabel, isCurrent)}
          style={{color: topImpactColor}}>
          {translate('tracking:scShockSensor.side')}
        </span>
        <ImpactTypeIcon impactType={impactArea} />
        <span className={themedClassName(classes.right, classes.currentLabel, isCurrent)}
          style={{color: topImpactColor}}>
          {translate('tracking:scShockSensor.side')}
        </span>
      </div>
      <span className={themedClassName(classes.impactLabel, classes.currentLabel, isCurrent)}
        style={{color: bottomImpactColor}}>
        {translate('tracking:scShockSensor.bottom')}
      </span>
    </div>
  );
};

export default ShockSensorGraphic;
